import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Card, PillButton, Input, Chip, IconCircle, EmptyState } from '../ui';
import { getTrustTokensToSend, clearTrustTokens } from '../utils/trustTokens';
import { ReactComponent as ChevronLeftIcon } from '../assets/icons/chevron-left.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as CircleCheckIcon } from '../assets/icons/circle-check.svg';
import { ReactComponent as AlertCircleIcon } from '../assets/icons/alert-circle.svg';
import { ReactComponent as MonitorIcon } from '../assets/icons/monitor.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import './Account.css';

// Mismo resumen de user-agent que Account.js (pattern de esa pantalla; no se
// extrae a util compartida por dos usos de quince líneas).
function summarizeUserAgent(ua) {
  if (!ua) return '—';
  let browser = 'Navegador';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  let os = '';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return os ? `${browser} · ${os}` : browser;
}

function relativeTime(dateStr, lang, neverLabel) {
  if (!dateStr) return neverLabel;
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.round(diffMs / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const units = [
    ['year', 31536000], ['month', 2592000], ['week', 604800],
    ['day', 86400], ['hour', 3600], ['minute', 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diffSec) >= secs) return rtf.format(-Math.round(diffSec / secs), unit);
  }
  return rtf.format(-diffSec, 'second');
}

function AccountSecurity() {
  const { t, i18n } = useTranslation();
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [newPinConfirm, setNewPinConfirm] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  const load = useCallback(() => {
    // Sin los trustTokens de este navegador el servidor no puede marcar
    // ningún dispositivo como "este dispositivo" (current): se los mandamos
    // siempre, aunque el usuario haya entrado por email o por PIN.
    const trustTokens = getTrustTokensToSend();
    const params = trustTokens.length ? { trustTokens: trustTokens.join(',') } : undefined;
    api.get('/account/security', { params })
      .then((res) => setSecurity(res.data))
      .catch(() => setSecurity(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePinSubmit = useCallback(async (e) => {
    e.preventDefault();
    setPinError('');
    if (!/^\d{6,12}$/.test(newPin)) { setPinError(t('account.security.pinLength')); return; }
    if (newPin !== newPinConfirm) { setPinError(t('account.security.pinMismatch')); return; }
    if (security?.hasPin && !currentPin) { setPinError(t('account.security.pinRequiredCurrent')); return; }
    setPinLoading(true);
    try {
      const body = { pin: newPin };
      if (security?.hasPin) body.currentPin = currentPin;
      // Cambiar el PIN revoca todos los trust tokens salvo el de "este
      // dispositivo" (currentTrustToken). No hay forma de saber con certeza
      // cuál de los guardados es el de este navegador para esta cuenta, así
      // que se manda el más reciente (el primero: saveTrustToken lo inserta
      // al principio). Si no hay ninguno guardado, el servidor los revoca
      // todos sin distinción y el propio se queda igual de inválido: se
      // vacía el array local para no arrastrar tokens muertos.
      const [primaryTrustToken] = getTrustTokensToSend();
      if (primaryTrustToken) body.currentTrustToken = primaryTrustToken;
      await api.put('/account/security/pin', body);
      if (!primaryTrustToken) clearTrustTokens();
      setCurrentPin(''); setNewPin(''); setNewPinConfirm('');
      window.alert(t('account.security.pinUpdated'));
      load();
    } catch (err) {
      setPinError(err.response?.data?.error || t('account.security.pinError'));
    } finally {
      setPinLoading(false);
    }
  }, [newPin, newPinConfirm, currentPin, security, t, load]);

  const handleRevoke = useCallback(async (device) => {
    const label = device.label || summarizeUserAgent(device.userAgent);
    if (!window.confirm(t('account.security.revokeConfirm', { label }))) return;
    try {
      await api.delete(`/account/security/trusted-devices/${device.id}`);
      // No hay forma de saber cuál de los trust tokens guardados en este
      // navegador era el de este dispositivo (el servidor solo guarda el
      // hash), así que al revocar el propio se vacían todos: uno revocado
      // no sirve para nada y no merece la pena guardar los demás a ciegas.
      if (device.current) clearTrustTokens();
      load();
    } catch {
      window.alert(t('account.security.revokeError'));
    }
  }, [t, load]);

  const handleRevokeAll = useCallback(async () => {
    if (!window.confirm(t('account.security.revokeAllConfirm'))) return;
    try {
      await api.post('/account/security/trusted-devices/revoke-all');
      clearTrustTokens();
      window.alert(t('account.security.revokeAllDone'));
      load();
    } catch {
      window.alert(t('account.security.revokeAllError'));
    }
  }, [t, load]);

  return (
    <div className="sgc-account">
      <Link to="/account" className="sgc-account__row-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
        <ChevronLeftIcon width={16} height={16} aria-hidden="true" /> {t('common.back')}
      </Link>

      <h1 className="sgc-account__section-title" style={{ fontSize: 20, margin: 0 }}>
        {t('account.security.title')}
      </h1>

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <>
          {security?.mustSetFactor && (
            <p className="sgc-login__error" style={{ background: 'rgba(245, 158, 11, 0.12)', color: 'var(--warn)' }}>
              {t('account.security.mustSetFactorNotice')}
            </p>
          )}

          {/* 1. Estado de los factores */}
          <Card>
            <h2 className="sgc-account__section-title">{t('account.security.statusTitle')}</h2>
            <div className="sgc-account__list">
              <div className="sgc-account__list-item">
                <IconCircle icon={LockIcon} tone={security?.hasPin ? 'success' : 'surface'} size={36} />
                <div className="sgc-account__list-info">
                  <p className="sgc-account__list-title">
                    {security?.hasPin ? t('account.security.pinSet') : t('account.security.pinNotSet')}
                  </p>
                </div>
              </div>
              <div className="sgc-account__list-item">
                <IconCircle icon={KeyIcon} tone={security?.hasMasterPassword ? 'success' : 'surface'} size={36} />
                <div className="sgc-account__list-info">
                  <p className="sgc-account__list-title">
                    {security?.hasMasterPassword ? t('account.security.masterPasswordSet') : t('account.security.masterPasswordNotSet')}
                  </p>
                </div>
              </div>
              <div className="sgc-account__list-item">
                <IconCircle icon={security?.verifiedEmail ? CircleCheckIcon : AlertCircleIcon} tone={security?.verifiedEmail ? 'success' : 'surface'} size={36} />
                <div className="sgc-account__list-info">
                  <p className="sgc-account__list-title">
                    {security?.verifiedEmail ? t('account.security.emailVerified') : t('account.security.emailNotVerified')}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* 2. Establecer / cambiar PIN */}
          <Card>
            <h2 className="sgc-account__section-title">{t('account.security.pinFormTitle')}</h2>
            <form className="sgc-account__pin-form" onSubmit={handlePinSubmit}>
              {security?.hasPin && (
                <Input
                  icon={LockIcon}
                  label={t('account.security.currentPinLabel')}
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="current-password"
                  maxLength={12}
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ''))}
                />
              )}
              <Input
                icon={LockIcon}
                label={t('account.security.newPinLabel')}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="new-password"
                maxLength={12}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              />
              <Input
                icon={LockIcon}
                label={t('account.security.confirmPinLabel')}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="new-password"
                maxLength={12}
                value={newPinConfirm}
                onChange={(e) => setNewPinConfirm(e.target.value.replace(/\D/g, ''))}
              />
              {pinError && <p className="sgc-login__error">{pinError}</p>}
              <PillButton type="submit" variant="primary" disabled={pinLoading}>
                {security?.hasPin ? t('account.security.pinSubmitChange') : t('account.security.pinSubmit')}
              </PillButton>
            </form>
          </Card>

          {/* 3. Dispositivos de confianza */}
          <Card>
            <h2 className="sgc-account__section-title">{t('account.security.trustedDevicesTitle')}</h2>
            {!security?.trustedDevices?.length ? (
              <EmptyState icon={MonitorIcon} title={t('account.security.noTrustedDevices')} />
            ) : (
              <div className="sgc-account__list">
                {security.trustedDevices.map((d) => (
                  <div key={d.id} className="sgc-account__list-item">
                    <IconCircle icon={MonitorIcon} tone="surface" size={36} />
                    <div className="sgc-account__list-info">
                      <p className="sgc-account__list-title">
                        {d.label || summarizeUserAgent(d.userAgent)}
                        {d.current && <Chip label={t('account.security.thisDevice')} selected />}
                      </p>
                      <p className="sgc-account__list-meta">
                        {t('account.security.lastUsed', { date: relativeTime(d.lastUsed, i18n.language, t('account.methods.never')) })}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="sgc-account__list-action"
                      onClick={() => handleRevoke(d)}
                      title={t('account.security.revoke')}
                    >
                      <TrashIcon width={16} height={16} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <PillButton variant="danger" className="sgc-account__add-method" onClick={handleRevokeAll}>
              {t('account.security.revokeAll')}
            </PillButton>
          </Card>
        </>
      )}
    </div>
  );
}

export default AccountSecurity;
